import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import PortalContainer, { PORTAL_MENU } from '../../../../components/container/PortalContainer'
import BlogDesktopMainPostSection from '../../../../components/landing/blog/BlogDesktopMainPostSection'
import BlogMobileMainPostSection from '../../../../components/landing/blog/BlogMobileMainPostSection'
import BlogPostListSection from '../../../../components/landing/blog/BlogPostListSection'
import { IBlogIndex, ISubMenuQuery } from '../../../../components/landing/blog/type'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { getEnv } from '../../../../modules/envUtil'
import { IS_DEVELOPMENT, IS_PRODUCTION, RINGLE_DOMAIN } from '../../../../modules/envVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import {
  LOCALE_EN,
  LOCALE_JA,
  LOCALE_KO,
  LOCALE_VI,
  LOCALE_ZH_CHS,
  LOCALE_ZH_CHT,
  SUPPORTED_LOCALES,
} from '../../../../modules/i18n/config'
import { s_menu } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import client from '../../../../modules/sanityClient'
import { buildSanityImage } from '../../../../modules/utils'
import {
  getBlogHomeGroqQueryAndParams,
  HELPCENTER_GET_MENU_DATA_QUERY,
  HELPCENTER_MAIN_POST_GET_DATA_QUERY,
} from '../../../../sanity/groqQueries'
import { BLOG_HOME_QUERY_MAIN_MENUResult, HELP_CENTER_GET_MENU_DATA_QUERYResult } from '../../../../sanity/types'
import { fetchSubMenu } from '../../student/landing/blog'

export const HelpCenterComponent = ({ menu, subMenu }) => {
  const CUSTOM_MENU_ID = 0
  const { t, locale } = useTranslation()
  const hashValue = window?.location?.hash.substring(1) || '' // "#"를 제거하기 위해 substring(1) 사용

  const router = useRouter()
  const [selectedMenu, setSelectedMenu] = useState(menu[0])
  const [data, setData] = useState([])
  const [selectedSubmenu, setSelectedSubmenu] = useState<any>({ _id: 0 })

  type CacheKey = [string, string | number, string | number, number, number, string | string[]]
  const cacheKey = (
    start: number,
    end: number,
    selectedMenuId: string | number,
    selectedSubMenuId: string | number,
    lang: string | string[]
  ): CacheKey => {
    return ['posts', selectedMenuId, selectedSubMenuId, start, end, lang]
  }

  const fetchFunction = async (
    start: number,
    end: number,
    selectedMenuId: string | number,
    selectedSubMenuId: string | number,
    lang
  ): Promise<BLOG_HOME_QUERY_MAIN_MENUResult[]> => {
    const { query, params } = getBlogHomeGroqQueryAndParams(start, end, selectedMenuId, selectedSubMenuId, lang)
    const posts: BLOG_HOME_QUERY_MAIN_MENUResult[] = await client.fetch(query, params)

    return posts
  }
  const { currentUser } = useCurrentUser()

  const { data: postsData } = useQuery({
    queryKey: cacheKey(0, 8, selectedMenu?._id, selectedSubmenu?._id, locale),
    queryFn: () => fetchFunction(0, 8, selectedMenu?._id, selectedSubmenu?._id, locale),
  })
  const handleCategory = (menu, i) => {
    router.replace(router.asPath.split('#')[0] + `#${i}`, router.asPath.split('#')[0] + `#${i}`, { shallow: true })

    setSelectedMenu(menu)
  }
  useEffect(() => {
    if (router.query.lang) {
      setSelectedMenu(getFirstSelectedMenu(router.query.type, menu))
    }
  }, [router.query.lang])

  useEffect(() => {
    const numbHashValue = Number(hashValue) || 0
    setSelectedMenu(menu[numbHashValue])
  }, [hashValue])

  useEffect(() => {
    if (postsData) {
      setData(postsData)
    }
  }, [postsData])

  const getFirstSelectedMenu = (type, menu) => {
    const hash = router.asPath?.split('#')[1] || 0
    if (hash) {
      return menu[hash]
    } else {
      return menu[0]
    }
  }
  const isMobile = useIsMobile()

  const getLanguageCode = useCallback((lang) => {
    switch (lang) {
      case 'kr':
        return 'ko'
      case 'zh_chs':
        return 'zh'
      case 'zh_cht':
        return 'de'
      default:
        return lang
    }
  }, [])

  const lang = useMemo(() => getLanguageCode(router.query.lang), [router.query.lang, getLanguageCode])

  useEffect(() => {
    if (!IS_PRODUCTION) return

    const baseConfig = {
      pluginKey: getEnv('NEXT_PUBLIC_PLUS_CHANNEL_IO_KEY'),
      language: lang,
      memberId: currentUser?.id,
      hideChannelButtonOnBoot: false,
      profile: {
        name: currentUser?.profile.name,
        email: currentUser?.profile.email,
        mobileNumber: currentUser?.profile.mobileNumber,
        avatarUrl: currentUser?.image_url,
        finished_lesson_count: currentUser?.profile.finished_lesson_count,
        point: currentUser?.student_card.points,
        unused_coupons: currentUser?.unused_coupons,
        last_purchased_date: currentUser?.braze?.purchase?.last_purchased_date,
        last_purchased_name: currentUser?.braze?.purchase?.last_purchased_name,
        customer_type: currentUser?.braze?.private?.customer_type,
        last_lesson_start_time: currentUser?.braze?.lesson?.last_lesson_start_time,
      },
    }

    console.log('BOOT_CHANNEL_TALK : ', baseConfig)
    window.ChannelIO?.('boot', baseConfig)
    window?.ChannelIO?.('showChannelButton')
    return () => {
      window.ChannelIO?.('shutdown')
      window?.ChannelIO?.('hideChannelButton')
    }
  }, [lang, currentUser])

  //서브 카테고리 리스트
  const subMenuList = useMemo(() => {
    return subMenu?.filter((subItem) => subItem.menu === selectedMenu?.title)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMenu, selectedMenu])

  const subCategoryList = subMenuList || []
  const sortedMenu = subCategoryList?.filter(Boolean).sort((a, b) => a.id - b.id)

  useEffect(() => {
    if (subMenuList) {
      setSelectedSubmenu(sortedMenu[0])
    }
  }, [subMenuList])

  return (
    <div className="mt-[40px] mb-[64px]">
      <div className="Hero4-32Bd px-[16px]">{t(s_menu.mypage.faq)}</div>
      <div className="mt-[20px]">
        <div className="border-b-1 sticky top-[0px] z-10 mb-[20px] flex h-[52px] flex-row border-gray-100 px-[16px]">
          <div className="flex w-full gap-[16px] bg-[rgba(255,255,255,0.85)] backdrop-blur-[5px]">
            {menu.map((listItem, i) => {
              return (
                <button
                  className={
                    selectedMenu == listItem
                      ? 'Body1-16Bd whitespace-nowrap border-b-2 border-b-black px-[8px] pt-[13px] pb-[11px]'
                      : 'Body1-16Bd  whitespace-nowrap px-[8px] py-[13px] text-gray-300'
                  }
                  key={i}
                  onClick={() => handleCategory(listItem, i)}
                >
                  {listItem.title}
                </button>
              )
            })}
          </div>
        </div>

        {selectedMenu?.mainPost &&
          (isMobile ? (
            <BlogMobileMainPostSection
              thumbnail={selectedMenu?.mainPost?.thumbnail}
              mainPost={selectedMenu?.mainPost}
            />
          ) : (
            <BlogDesktopMainPostSection
              thumbnail={selectedMenu?.mainPost?.thumbnail}
              mainPost={selectedMenu?.mainPost}
            />
          ))}
        <div className="Normal-Pagination mt-[20px]">
          <div className="mb-[20px] flex flex-row gap-[8px] px-[16px] sm:px-[0px] flex-nowrap overflow-scroll no-scrollbar">
            {sortedMenu?.map((subItem, i) => {
              return (
                <button
                  className={classNames(
                    subItem._id === selectedSubmenu?._id
                      ? 'whitespace-nowrap Body1-16Bd rounded-[8px] border-[1.5px] border-purple-500 bg-purple-50 px-[16px] py-[6.5px] text-purple-500'
                      : 'whitespace-nowrap Body1-16Md rounded-[8px] border-[1.5px] border-gray-300 px-[16px] py-[6.5px]'
                  )}
                  key={i}
                  onClick={() => setSelectedSubmenu(subItem)}
                >
                  {subItem.title}
                </button>
              )
            })}
          </div>
          <BlogPostListSection
            isMobile={isMobile}
            posts={data}
            fetchFunction={fetchFunction}
            cacheKey={cacheKey}
            menuId={selectedMenu?._id}
            subMenuId={selectedSubmenu?._id}
            menuForm={selectedMenu?.form}
          />
        </div>
      </div>
    </div>
  )
}

const Index = ({ seoData, ogData }: IBlogIndex) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { data: menu } = useQuery({
    queryKey: ['menu', router.query.lang],
    queryFn: () => fetchMenu(router.query.lang),
  })

  const { data: subMenu }: ISubMenuQuery = useQuery({ queryKey: ['subMenu', router.query.lang], queryFn: fetchSubMenu })

  const hashValue = window?.location?.hash.substring(1) || '' // "#"를 제거하기 위해 substring(1) 사용
  const isFAQ = hashValue == '1'
  return (
    <>
      <PortalContainer
        layoutClassName="no-scrollbar mx-auto mb-[120px] w-full max-w-[1040px]"
        pageId={isFAQ ? PORTAL_MENU.MENU_FAQ : PORTAL_MENU.MENU_HELPCENTER}
        onBackClick={() => {
          router.back()
        }}
      >
        {menu && <HelpCenterComponent menu={menu} subMenu={subMenu} />}
      </PortalContainer>
    </>
  )
}

const getTypeForLanguage = (lang) => {
  if (lang === LOCALE_KO) return 'blogMainPost'
  if (lang === LOCALE_EN) return 'blogMainPostEn'
  if (lang === LOCALE_JA) return 'blogMainPostJa'
  if (lang === LOCALE_VI) return 'blogMainPostVi'
  if (lang === LOCALE_ZH_CHS) return 'blogMainPostZhs'
  if (lang === LOCALE_ZH_CHT) return 'blogMainPostZht'
  return 'blogMainPost'
}

// Fetch function for mainPostData
const fetchMainPost = async (lang) => {
  const type = getTypeForLanguage(lang)

  const params = {
    type: type,
  }

  const response = await client.fetch(HELPCENTER_MAIN_POST_GET_DATA_QUERY, params)
  return response
}
// Fetch function for menu
const fetchMenu = async (lang) => {
  const params = {
    lang: lang,
  }

  const menu: HELP_CENTER_GET_MENU_DATA_QUERYResult = await client.fetch(HELPCENTER_GET_MENU_DATA_QUERY, params)
  return menu
}

export const getStaticPaths: GetStaticPaths = () => {
  if (!IS_PRODUCTION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const paths = SUPPORTED_LOCALES.map((locale) => ({
    params: { lang: locale },
  }))

  return { paths, fallback: 'blocking' }
}
// Get server-side props function
export const getStaticProps: GetStaticProps = async (props) => {
  const queryClient = new QueryClient()
  const { params } = props

  // Fetch mainPostData before prefetching other queries
  const mainPostData = await fetchMainPost(params.lang)

  // Prefetch queries using queryClient.prefetchQuery
  await Promise.all([queryClient.prefetchQuery(['menu', params.lang], () => fetchMenu(params.lang))])

  // Extract metaData from mainPostData for SEO
  const metaData = mainPostData[0]?.metaData

  // Define SEO and Open Graph data
  const seoData = {
    pageTitle: metaData?.metaTitle,
    pageDescription: metaData?.metaDescription,
    pageKeywords: metaData?.metaKeyword,
  }

  const ogData = {
    title: metaData?.ogTitle,
    desc: metaData?.ogDescription,
    image: buildSanityImage(mainPostData[0].ogImage).url(),
    ogUrl: `https://${IS_DEVELOPMENT ? 'staging.ringlefront.com' : RINGLE_DOMAIN}/ko/student/landing/blog`,
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      mainPostData,
      seoData,
      ogData,
    },
    revalidate: 900,
  }
}

export default Index
